<template>
  <div>
    <PageHeaderBanner>
      <template #title-toggle>
        <q-btn-toggle
          :model-value="type"
          flat
          dense
          size="1.6rem"
          text-color="grey-7"
          toggle-color="primary"
          :options="[
            { label: 'キー検索', value: KEY },
            { label: '構造検索', value: STRUCTUAL },
          ]"
          class="page-toggle"
          @update:model-value="
            (value: any) => {
              router.replace({
                name: 'search',
                params: { type: value, supCategory: props.supCategory },
              });
            }
          "
        />
      </template>
    </PageHeaderBanner>
    <q-expansion-item v-model="openFlg" header-style="display:none">
      <div class="csr00200-search-form">
        <!-- カテゴリー -->
        <div class="row q-mb-sm">
          <CategoryCheckBox
            v-model:categories="categories"
            class="q-mb-md"
            @change="changeCategory"
          />
        </div>
        <div class="row">
          <!-- キー検索条件 -->
          <div v-if="type === KEY" class="col-12">
            <KeySearchForm
              v-model:freeWord="freeWord"
              v-model:keyData="keyData"
              v-model:keyValue="keyValue"
              v-model:completeMatch="completeMatch"
              @clear-free-word="clearFreeWord"
              @clear-key-search="clearKeySearch"
              @change-free-word="freeWord = $event"
              @change-key-data="changeKeyData"
              @change-key-value="keyValue = $event"
              @change-complete-match="completeMatch = $event"
            />
          </div>
          <!-- 構造検索条件 -->
          <div v-if="type === STRUCTUAL" :class="[type === STRUCTUAL ? 'structual-leftarea' : '']">
            <StructureSearchForm
              v-model:editorFlg="editorFlg"
              v-model:sdfFile="sdfFile"
              @change-editor-flg="editorFlg = $event"
              @change-sdf-file="sdfFile = $event"
            />
          </div>
          <div :class="[type === STRUCTUAL ? 'structual-rightarea' : 'col-12']">
            <div v-if="type === STRUCTUAL">
              <p class="construct-ondition">抽出条件</p>
              <StructureConditionsForm
                v-model:searchType="searchType"
                v-model:ez="ez"
                v-model:chiral="chiral"
                v-model:tautomer="tautomer"
                v-model:threshold="threshold"
                @change-search-type="searchType = $event"
                @change-ez="ez = $event"
                @change-chiral="chiral = $event"
                @change-tautomer="tautomer = $event"
                @change-threshold="threshold = $event"
              />
            </div>
            <!-- サプライヤ -->
            <div class="csr00200-supplier">
              <p class="q-mb-xs" style="font-weight: 600">サプライヤ</p>
              <SupplierSelectsUseInput
                v-model:suppliers="suppliers"
                @clear="suppliers = $event"
                @change="suppliers = $event"
              />
            </div>

            <!-- 検索、クリアボタン -->
            <div class="row justify-end q-mt-md q-gutter-xs">
              <q-btn
                v-if="type === KEY"
                color="primary"
                style="width: 150px"
                label="検索"
                icon="mdi-magnify"
                @click="keySearch"
              />
              <q-btn
                v-if="type === STRUCTUAL"
                color="primary"
                style="width: 150px"
                label="検索"
                icon="mdi-magnify"
                @click="structureSearch"
              />
              <q-btn
                v-if="type === KEY"
                outline
                style="width: 150px; color: var(--q-delete)"
                label="クリア"
                icon="mdi-eraser"
                @click="keySearchClear"
              />
              <q-btn
                v-if="type === STRUCTUAL"
                outline
                style="width: 150px; color: var(--q-delete)"
                label="クリア"
                icon="mdi-eraser"
                @click="structureSearchClear"
              />
            </div>
          </div>
        </div>
      </div>
    </q-expansion-item>
    <!-- ダイアログ -->
    <AlertDialog
      :display="!searchableFlg"
      level="error"
      :title="dialogTitle"
      :text="dialogText"
      @complete="searchableFlg = true"
    />
    <AlertDialog
      :display="resultOverFlg"
      level="warning"
      confirmation
      :title="dialogTitle"
      :text="dialogText"
      @complete="closeDialog"
    />
    <!-- 検索結果 -->
    <div v-if="searchFlg" ref="searchResultElement" class="csr00200-expansion-bar">
      <q-btn color="primary" flat class="full-width" @click="conditionAreaOpen()"
        ><q-icon left :name="openFlg ? 'mdi-chevron-double-up' : 'mdi-chevron-double-down'" />
      </q-btn>
    </div>
    <SearchResultList
      v-if="searchFlg"
      :search-result="searchResult"
      :reagent-flg="reagentFlg"
      :categories="categories"
      :suppliers="suppliers"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import KeySearchForm from '../components/form/KeySearchForm.vue';
import StructureSearchForm from '../components/form/StructureSearchForm.vue';
import StructureConditionsForm from '../components/form/StructureConditionsForm.vue';
import PageHeaderBanner from '../components/banner/PageHeaderBanner.vue';
import CategoryCheckBox from '../components/checkBox/CategoryCheckBox.vue';
import SupplierSelectsUseInput from '../components/selects/SupplierSelectsUseInput.vue';
import SearchResultList from '../components/list/SearchResultList.vue';
import AlertDialog from '../components/dialogs/AlertDialog.vue';
import http from '../common/axios-common';
import replaceFullToHalf from '../common/utils/stringUtils';
import { getMarvin, clearMarvin } from '../common/marvin-js';
import HttpResponseStatusCode from '../common/code/HttpResponseStatusCode';
import ResultStatusCode from '../common/code/ResultStatusCode';
import messages from '../messages/message.json';
import { isFileExist } from '../common/utils/fileUtils';
import DetailResultStatusCode from '../common/code/DetailResultStatusCode';

interface Props {
  // タイプ（キー検索:0[デフォルト値], 構造検索:1）
  type: string;
  // カテゴリー(試薬:1[デフォルト値])
  supCategory: number;
}

const props = withDefaults(defineProps<Props>(), {
  type: '0',
  supCategory: 1,
});

const router = useRouter();

// キー検索
const KEY = '0';

// 構造検索
const STRUCTUAL = '1';

// 試薬
const REAGENT = 1;

// 開閉
const openFlg = ref<boolean>(true);

// 検索結果
const searchFlg = ref<boolean>(false);

// サプライヤInterface
interface Supplier {
  label: string;
  value: string;
}

// サプライヤ
const suppliers = ref<Supplier[]>([]);

// カテゴリー
const categories = ref<number[]>([]);

// 試薬フラグ
const reagentFlg = ref<boolean>(true);

// フリーワード
const freeWord = ref<string>('');

// 詳細検索キー
interface KeyPullDown {
  label: string;
  value: string;
}

// 詳細検索項目
const keyData = ref<KeyPullDown>({ label: '', value: '' });

// 詳細検索値
const keyValue = ref<string>('');

// 完全一致
const completeMatch = ref<boolean>(true);

// 検索可能フラグ
const searchableFlg = ref<boolean>(true);

// 検索結果要素
const searchResultElement = ref<HTMLImageElement>();

// 検索結果上限超過フラグ
const resultOverFlg = ref<boolean>(false);

// ダイアログテキスト
const dialogText = ref<Array<string>>(['']);

// ダイアログタイトル
const dialogTitle = ref<string>('');

// Dialog表示レベル
const dialogLevel = ref('');

// Marvin Editor FLG
const editorFlg = ref<boolean>(true);

// SDFファイル
const sdfFile = ref<File>();

// 検索タイプ
const searchType = ref<string>('partial');

// E/Z一致
const ez = ref<boolean>(true);

// Chiral一致
const chiral = ref<boolean>(true);

// Tautomer
const tautomer = ref<boolean>(false);

// 閾値
const threshold = ref<number>(70);

// ダイアログ閉じる
const closeDialog = () => {
  resultOverFlg.value = false;
  setTimeout(() => searchResultElement.value?.scrollIntoView(true), 600);
};

// 検索結果
interface Result {
  atto_id: string;
  atto_supplier_count: number;
  order_count: number;
  condition_id: number;
  result_id: number;
  result_number: number;
  structure: string;
}

// 検索条件
interface Conditions {
  condition_id: number;
  condition_number: number;
  symbolic_query: string;
  symbolic_query_type: string;
  querysearchResultCount: number;
}

// 検索結果情報
interface SearchResultInfo {
  searchResultConditionsDto: Conditions;
  searchResultDetailList: Array<Result>;
}

// 検索Response
interface SearchResponse {
  attoIdList: Array<string>;
  searchResultInfo: Array<SearchResultInfo>;
  searchMaxCount: number;
}

// 検索結果
const searchResult = ref<SearchResponse>({
  attoIdList: [],
  searchResultInfo: [],
  searchMaxCount: 0,
});

// カテゴリー初期値設定
const initCategory = () => {
  // カテゴリー設定
  categories.value.push(Number(props.supCategory));
  // カテゴリーが試薬の場合
  if (props.supCategory === REAGENT) {
    reagentFlg.value = true;
  } else {
    reagentFlg.value = false;
  }
};

// カテゴリー変更
const changeCategory = (value: number[]) => {
  categories.value = value;
};

// フリーワードクリア
const clearFreeWord = () => {
  freeWord.value = '';
};

// 詳細条件クリア
const clearKeySearch = () => {
  keyData.value = { label: '', value: '' };
  keyValue.value = '';
  completeMatch.value = true;
};

// 詳細条件のkey変更
const changeKeyData = (value: KeyPullDown) => {
  keyData.value = value;
  if (keyData.value && keyData.value.label !== '化学名') {
    completeMatch.value = true;
  }
};

// キー検索条件クリア
const keySearchClear = () => {
  suppliers.value = [];
  categories.value = [];
  initCategory();
  freeWord.value = '';
  keyData.value = { label: '', value: '' };
  keyValue.value = '';
  completeMatch.value = true;
  searchFlg.value = false;
};

// 検索件数上限超過チェック
const checkOverResult = () => {
  if (searchResult.value.searchMaxCount === Number(messages.ja.validation.search_max_count)) {
    dialogText.value = [
      `先頭から${messages.ja.validation.search_max_count}件までの検索結果を表示しています。`,
      'お探しの化合物が見つからない場合、検索条件を絞り込んで再度検索してください。',
    ];
    dialogTitle.value = '検索結果上限値超過';
    resultOverFlg.value = true;
  }
};

// キー検索
const keySearch = () => {
  let arr: string[] = [];
  let fixArr: string[] = [];

  if (keyValue.value.match(/[']/) && keyData.value.value === 'SMILES') {
    dialogTitle.value = '検索条件入力エラー';
    dialogText.value = ["SMILESで検索時には「'」を入力しないでください。"];
    searchableFlg.value = false;
  }

  // 詳細検索入力内容リストに整形
  if (keyValue.value !== '') {
    // 全角から半角へ変換
    let str = replaceFullToHalf(keyValue.value);
    str = str.replaceAll(/[ー]/g, '-');
    arr = str.split('\n');
    // ブランク削除
    arr.forEach((element) => {
      str = element.trim();
      if (str) {
        fixArr.push(str);
      }
    });
    fixArr = fixArr.filter((v) => !''.includes(v));
  }

  // 必須項目が入力されていない場合
  if (
    categories.value.length === 0 ||
    (freeWord.value.trim() === '' && (keyData.value.value === '' || fixArr.length === 0))
  ) {
    dialogTitle.value = '検索条件入力エラー';
    dialogText.value = [
      'カテゴリーを１つ以上選択し、簡易検索もしくは詳細検索条件を入力してください。',
    ];
    searchableFlg.value = false;
  }

  // 詳細検索テキストチェック
  if (fixArr.length > Number(messages.ja.validation.search_max_count)) {
    dialogTitle.value = '検索条件入力エラー';
    dialogText.value = [
      `詳細検索条件が${messages.ja.validation.search_max_count}行を超えています。`,
      `${messages.ja.validation.search_max_count}行以内で入力してください。`,
    ];
    searchableFlg.value = false;
  }

  // 簡易検索条件、詳細検索条件の両方入力されている場合
  if (freeWord.value !== '' && (keyData.value.value !== '' || fixArr.length !== 0)) {
    dialogTitle.value = '検索条件入力エラー';
    dialogText.value = ['検索条件は簡易検索・詳細検索のいずれかを入力してください。'];
    searchableFlg.value = false;
  }

  if (searchableFlg.value) {
    // Loading Start
    Loading.show();
    searchFlg.value = false;

    // バックエンドへ渡すパラメータ設定
    const formData = new FormData();
    formData.append('query', freeWord.value);
    formData.append('conditionType', keyData.value.value);
    fixArr.forEach((query) => formData.append('queries', query));

    if (completeMatch.value) {
      formData.append('completeMatch', 'true');
    } else {
      formData.append('completeMatch', 'false');
    }
    if (freeWord.value !== '') {
      formData.append('type', 'TEXT');
    } else {
      formData.append('type', 'BULK');
    }
    categories.value.forEach((category) =>
      formData.append('sup_category_ids', category.toString())
    );
    suppliers.value.forEach((supplier: { label: string; value: string }) => {
      formData.append('supplier_ids', supplier.value);
    });
    // 検索
    http
      .post('/csr002001/simpleSearch', formData)
      .then((response) => {
        if (response.data.common.status === ResultStatusCode.ERROR) {
          if (response.data.common.detail_status === DetailResultStatusCode.STATEMENT_TIMEOUT) {
            dialogTitle.value = '検索時間超過エラー';
            dialogText.value = [`検索時間が上限に達しました。条件を見直して再検索してください。`];
            searchableFlg.value = false;
          } else {
            // エラー画面遷移
            router.push({ name: 'error' });
          }
        } else {
          searchFlg.value = true;
          searchResult.value = response.data;
          if (Number(categories.value[0]) === REAGENT) {
            reagentFlg.value = true;
          } else {
            reagentFlg.value = false;
          }
          checkOverResult();
        }
      })
      .catch((err) => {
        if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
          // セッションタイムアウトの場合
          router.push({ name: 'login', query: { timeout: 'timeout' } });
        } else {
          // その他エラーの場合
          router.push({ name: 'error' });
        }
      })
      .finally(() => {
        // Loading End
        Loading.hide();
        // スクロール
        setTimeout(() => searchResultElement.value?.scrollIntoView(true), 800);
      });
  }
};

// 構造検索
const structureSearch = () => {
  getMarvin().then(async (structure: string) => {
    // MarvinJs読み込み失敗
    if (structure === 'error') {
      dialogTitle.value = 'MarvinJs読み込みエラー';
      dialogText.value = ['構造式の取得に失敗しました。', '画面を開き直して再度お試しください。'];
      searchableFlg.value = false;
    }
    // 必須項目が入力されていない場合
    if (
      categories.value.length === 0 ||
      (structure === undefined && editorFlg.value) ||
      (sdfFile.value === undefined && !editorFlg.value)
    ) {
      dialogTitle.value = '検索条件入力エラー';
      if (editorFlg.value) {
        dialogText.value = ['カテゴリーを１つ以上選択し、構造式を入力してください。'];
      }
      if (!editorFlg.value) {
        dialogText.value = ['カテゴリーを１つ以上選択し、SDFileをアップロードしてください。'];
      }
      searchableFlg.value = false;
    }

    // SDF読み込みエラー
    if (sdfFile.value) {
      await isFileExist(sdfFile.value).then((value) => {
        if (!value) {
          dialogTitle.value = 'ファイル読み込みエラー';
          dialogLevel.value = 'error';
          if (sdfFile.value !== undefined && sdfFile.value.size === 0) {
            dialogText.value = [
              '選択したファイルは内容が設定されていません。',
              'ファイルを再度選択してください。',
            ];
          } else {
            dialogText.value = [
              '更新または削除されたファイルがあるため、アップロードできませんでした。',
              'ファイルを再度選択してください。',
            ];
          }
          searchableFlg.value = false;
        }
      });
    }

    // 閾値入力範囲チェック
    if (threshold.value < 1 || (threshold.value > 100 && searchType.value === 'similar')) {
      dialogTitle.value = '検索条件入力エラー';
      dialogText.value = ['類似率は1～100の整数値で入力してください。'];
      searchableFlg.value = false;
    }

    // 構造式もしくはSDFが選択されている場合
    if (searchableFlg.value) {
      // Loading Start
      Loading.show();
      searchFlg.value = false;

      const formData = new FormData();
      if (ez.value) {
        formData.append('ez', 'true');
      } else {
        formData.append('ez', 'false');
      }
      if (chiral.value) {
        formData.append('chiral', 'true');
      } else {
        formData.append('chiral', 'false');
      }
      if (tautomer.value) {
        formData.append('tautomer', 'true');
      } else {
        formData.append('tautomer', 'false');
      }
      formData.append('threshold', String(threshold.value / 100));
      formData.append('searchType', searchType.value);
      formData.append('type', 'STRUCTURE');
      if (structure !== undefined && editorFlg.value) {
        formData.append('structure', structure);
        formData.append('extension', 'STRUCTURE');
      }
      if (sdfFile.value !== undefined && !editorFlg.value) {
        formData.append('sdfFile', sdfFile.value);
        formData.append('extension', 'SDF');
      }
      categories.value.forEach((category) =>
        formData.append('sup_category_ids', category.toString())
      );
      suppliers.value.forEach((supplier: { label: string; value: string }) => {
        formData.append('supplier_ids', supplier.value);
      });
      // 検索
      http
        .post('/csr002002/structureSerach', formData)
        .then((response) => {
          if (response.data.common.status === ResultStatusCode.ERROR) {
            if (response.data.common.detail_status === DetailResultStatusCode.MAX_MOL_ERROR) {
              dialogTitle.value = '検索条件入力エラー';
              dialogText.value = [
                `SDFile内の化合物が${messages.ja.validation.search_max_count}件を超えています。`,
                `SDFile内の件数を${messages.ja.validation.search_max_count}件以内にして検索してください。`,
              ];
              searchableFlg.value = false;
            } else if (
              response.data.common.detail_status === DetailResultStatusCode.STATEMENT_TIMEOUT
            ) {
              dialogTitle.value = '検索時間超過エラー';
              dialogText.value = [`検索時間が上限に達しました。条件を見直して再検索してください。`];
              searchableFlg.value = false;
            } else if (
              response.data.common.detail_status === DetailResultStatusCode.PARAMETER_ERROR
            ) {
              dialogTitle.value = '検索条件入力エラー';
              dialogText.value = [
                'SDFileが不正です。',
                `カテゴリーを１つ以上選択し、正しいSDFileをアップロードしてください。`,
              ];
              searchableFlg.value = false;
            } else {
              // エラー画面遷移
              router.push({ name: 'error' });
            }
          } else {
            searchFlg.value = true;
            searchResult.value = response.data;
            if (Number(categories.value[0]) === REAGENT) {
              reagentFlg.value = true;
            } else {
              reagentFlg.value = false;
            }
            checkOverResult();
          }
        })
        .catch((err) => {
          if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
            // セッションタイムアウトの場合
            router.push({ name: 'login', query: { timeout: 'timeout' } });
          } else {
            // その他エラーの場合
            router.push({ name: 'error' });
          }
        })
        .finally(() => {
          // Loading End
          Loading.hide();
          // スクロール
          setTimeout(() => searchResultElement.value?.scrollIntoView(true), 800);
        });
    }
  });
};

// 構造検索条件クリア
const structureSearchClear = () => {
  clearMarvin().then((err: string) => {
    // MarvinJs読み込み失敗
    if (err === 'error') {
      dialogTitle.value = 'MarvinJs読み込みエラー';
      dialogText.value = ['構造式のクリアに失敗しました。', '画面を開き直して再度お試しください。'];
      searchableFlg.value = false;
    }
  });
  suppliers.value = [];
  categories.value = [];
  initCategory();
  searchType.value = 'partial';
  sdfFile.value = undefined;
  ez.value = true;
  chiral.value = true;
  tautomer.value = false;
  threshold.value = 70;
  searchFlg.value = false;
};

// 検索結果表示
const conditionAreaOpen = () => {
  if (openFlg.value) {
    openFlg.value = false;
  } else {
    openFlg.value = true;
  }
};

// 初期表示処理
initCategory();
</script>
<style scoped>
.csr00200-search-form {
  width: 80%;
  max-width: 1100px;
  margin: 0 auto;
}
.csr00200-expansion-bar {
  border-bottom: 1px solid var(--q-primary);
}

.q-expansion-item--expanded + .csr00200-expansion-bar {
  margin-top: 40px;
}

.page-header-banner-page-title.row {
  padding-top: 14px;
}

.structual-leftarea {
  width: 55%;
  margin-right: 2%;
}

.structual-rightarea {
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  width: 43%;
  min-height: 412px;
}

.construct-ondition {
  font-size: 1.1rem;
  letter-spacing: 2px;
  border-bottom: 1px solid black;
  padding-left: 10px;
  font-weight: bold;
}
</style>
<style>
.page-header-banner-page-title .q-btn-group.page-toggle button {
  border-bottom: 3px solid transparent;
  padding: 0 12px;
}
.page-header-banner-page-title .q-btn-group.page-toggle .q-btn.text-primary {
  border-color: var(--q-primary);
}
</style>
